import { AudioManager } from "../../AudioManager";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { BoardBackground } from "./BoardBackground";
import { BoardManager } from "./BoardManager";
import { Orb } from "./orbs/Orb";
import { OrbsPool } from "./orbs/OrbsPool";
import * as Candy from "@ravalmatic/candy-engine";

export class BoardContainer extends Phaser.GameObjects.Container {

    public static currentInstance: BoardContainer;

    public static ORBS_AFFECTED_BY_WRAPPED_EXPLOSION = [

        {p: {c: -2, r: -2}, delta: {x: -.25, y: -.25}}, {p: {c: -1, r: -2}, delta: {x: 0, y: -.3}}, {p: {c: 0, r: -2}, delta: {x: 0, y: -.35}}, {p: {c: 1, r: -2}, delta: {x: 0, y: -.3}}, {p: {c: 2, r: -2}, delta: {x: .25, y: -.25}},
        {p: {c: -2, r: -1}, delta: {x: -.3, y: 0}}, {p: {c: 2, r: -1}, delta: {x: .3, y: 0}},
        {p: {c: -2, r: 0}, delta: {x: -.35, y: 0}}, {p: {c: 2, r: 0}, delta: {x: .35, y: 0}},
        {p: {c: -2, r: 1}, delta: {x: -.3, y: 0}}, {p: {c: 2, r: 1}, delta: {x: .3, y: 0}},
        {p: {c: -2, r: 2}, delta: {x: -.25, y: .25}}, {p: {c: -1, r: 2}, delta: {x: 0, y: .35}}, {p: {c: 0, r: 2}, delta: {x: 0, y: .35}}, {p: {c: 1, r: 2}, delta: {x: 0, y: .35}}, {p: {c: 2, r: 2}, delta: {x: .25, y: .25}},

        {p: {c: -3, r: -3}, delta: {x: -.1, y: -.1}}, {p: {c: -2, r: -3}, delta: {x: 0, y: -.15}}, {p: {c: -1, r: -3}, delta: {x: 0, y: -.15}}, {p: {c: 0, r: -3}, delta: {x: 0, y: -.2}}, {p: {c: 1, r: -3}, delta: {x: 0, y: -.15}}, {p: {c: 2, r: -3}, delta: {x: 0, y: -.15}}, {p: {c: 3, r: -3}, delta: {x: .1, y: -.1}},
        {p: {c: -3, r: -2}, delta: {x: -.15, y: 0}}, {p: {c: 3, r: -2}, delta: {x: .15, y: 0}},
        {p: {c: -3, r: -1}, delta: {x: -.15, y: 0}}, {p: {c: 3, r: -1}, delta: {x: .15, y: 0}},
        {p: {c: -3, r: 0}, delta: {x: -.2, y: 0}}, {p: {c: 3, r: 0}, delta: {x: .2, y: 0}},
        {p: {c: -3, r: 1}, delta: {x: -.15, y: 0}}, {p: {c: 3, r: 1}, delta: {x: .15, y: 0}},
        {p: {c: -3, r: 2}, delta: {x: -.15, y: 0}}, {p: {c: 3, r: 2}, delta: {x: .15, y: 0}},
        {p: {c: -3, r: 3}, delta: {x: -.1, y: .1}}, {p: {c: -2, r: 3}, delta: {x: 0, y: .15}}, {p: {c: -1, r: 3}, delta: {x: 0, y: .15}}, {p: {c: 0, r: 3}, delta: {x: 0, y: .2}}, {p: {c: 1, r: 3}, delta: {x: 0, y: .15}}, {p: {c: 2, r: 3}, delta: {x: 0, y: .15}}, {p: {c: 3, r: 3}, delta: {x: .1, y: .1}},
    ];

    public orbsContainer: Phaser.GameObjects.Container;
    public areOrbsDisplacedByWrappedImplosion: boolean;

    private orbs: Orb[];
    private obsPool: OrbsPool;
    private boardBackground: BoardBackground;
    private totalOrbsImploding: number;
    private totalOrbsMoving: number;
    private orbsMovingCounter: number;

    private wrappedImplosionDisplacementFactor: number;
    
    constructor(scene: Phaser.Scene) {
         
        super(scene);

        BoardContainer.currentInstance = this;

        this.obsPool = new OrbsPool(this.scene, this, 80);
        
        this.createAnimations();

        this.orbs = [];
        this.areOrbsDisplacedByWrappedImplosion = false;
        this.wrappedImplosionDisplacementFactor = 1;

        this.boardBackground = new BoardBackground(this.scene);
        this.add(this.boardBackground);

        this.orbsContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.orbsContainer);

        for (let c = 0; c < GameVars.cols; c ++) {
            for (let r = 0; r < GameVars.rows; r ++) {
                const orbData = GameVars.candyEngine.board[c][r];
                const orb = this.obsPool.getOrb(orbData, {c: c, r: r});
                this.orbsContainer.add(orb);
                this.orbs.push(orb);
            }
        }

        this.onLevelUp(true);
    }

    public update(): void {

        this.orbs.forEach((orb: Orb): void => {
            orb.update();
        });

        this.areOrbsDisplacedByWrappedImplosion = false;

        for (let i = 0; i < this.orbs.length; i ++) {

            if (this.orbs[i].displacedByWrappedImplosion) {
                this.areOrbsDisplacedByWrappedImplosion = true;
                break;
            }
        }

        this.boardBackground.update();
    }

    public onFirstOrbSelected(): void {

        const p1 = BoardManager.currentInstance.p1;

        const orb1 = this.getOrbAt(p1);
        orb1.select();
    }

    public onMatch(): void {
        
        const p1 = BoardManager.currentInstance.p1;
        const p2 = BoardManager.currentInstance.p2;

        const orb1 = this.getOrbAt(p1);
        const orb2 = this.getOrbAt(p2);

        orb1.onMatch(p2, false);
        orb2.onMatch(p1, true);
    }

    public onMissmatch(): void {
        
        const p1 = BoardManager.currentInstance.p1;
        const p2 = BoardManager.currentInstance.p2;

        const orb1 = this.getOrbAt(p1);
        const orb2 = this.getOrbAt(p2);

        orb1.onMismatch(p2);
        orb2.onMismatch(p1);
    }

    public onNotAdjacentOrbsSelected(): void {

        const p1 = BoardManager.currentInstance.p1;
        const orb1 = this.getOrbAt(p1);
        orb1.unSelect();
    }

    public onOrbImploded(orb: Orb): void {

        this.totalOrbsImploding --;

        if (this.totalOrbsImploding === 0) {

            this.scene.time.delayedCall(100, function(): void {
                BoardManager.currentInstance.spawnAndAnimateorbs();
            }, [], this);    
        }
    }

    public removeOrbsAt(eliminatedOrbs: Candy.EliminatedOrb []): void {

        this.totalOrbsImploding = 0;
        this.wrappedImplosionDisplacementFactor = 1;

        for (let i = 0; i < eliminatedOrbs.length; i ++) {

            const eliminatedOrbData = eliminatedOrbs[i];

            const orb = this.getOrbAt(eliminatedOrbData.p);      

            if (orb && !orb.setToImplode) {

                orb.implode(eliminatedOrbData); 
            
                this.totalOrbsImploding ++;

            } else {
                console.log("ERROR! NO HAY ORB EN ESTA POSICION ", "c:", eliminatedOrbData.p.c, " r:", eliminatedOrbData.p.r);
            }
        }
    }

    public mutateOrbsAt(mutatedOrbs: Candy.MutatedOrb[]): void {

        for (let i = 0; i < mutatedOrbs.length; i ++) {
            const orb = this.getOrbAt(mutatedOrbs[i].p);
            orb.mutateTo(mutatedOrbs[i].type, mutatedOrbs[i].n * 3 * Math.floor (1 / GameVars.speedFactor), mutatedOrbs[i].pBomb);
        }
    }

    public onOrbReachedItsPosition(): void {
        
        this.orbsMovingCounter ++;

        if (this.orbsMovingCounter === this.totalOrbsMoving) {
            
            this.boardBackground.onFallingOrbsAnimationCompleted();
            
            BoardManager.currentInstance.onFallingOrbsAnimationCompleted();
        }
    }

    public removeOrb(orb: Orb): void {

        const index = this.orbs.indexOf(orb);
    
        if (index !== -1) {
            this.orbs.splice(index, 1);
        }

        this.orbsContainer.remove(orb);

        this.obsPool.onOrbRemovedFromBoard(orb);
    }

    public spawnAndAnimateOrbs(displacementData: Candy.OrbDisplacementData []): void {

        this.totalOrbsMoving = displacementData.length;
        this.orbsMovingCounter = 0;

        let orb: Orb;

        for (let i = 0; i < displacementData.length; i ++) {

            const p = {c: displacementData[i].c, r: displacementData[i].r};

            if (p.r >= 0) {

                orb = this.getOrbAt(p);
                orb.move(displacementData[i].d, 0);

            } else {
                
                // pillamos un Orb del pool
                orb = this.obsPool.getOrb({id: displacementData[i].id, color: displacementData[i].color, type: 0, prevPos: {c: p.c, r: p.r}, mutated: false, removed: false}, p);
                this.orbsContainer.add(orb);

                this.orbs.push(orb);

                orb.move(displacementData[i].d, 5);

                AudioManager.playSound("new_candy_enters");
            }
        }
    }

    public onWrappedOrbImploded(p: {c: number, r: number}): void {

        for (let i  = 0; i < BoardContainer.ORBS_AFFECTED_BY_WRAPPED_EXPLOSION.length; i ++) {

            const c = BoardContainer.ORBS_AFFECTED_BY_WRAPPED_EXPLOSION[i].p.c + p.c;
            const r = BoardContainer.ORBS_AFFECTED_BY_WRAPPED_EXPLOSION[i].p.r + p.r;

            const cols = GameVars.cols;
            const rows = GameVars.rows;

            if (c >= 0 && c < cols && r >= 0 && r < rows) {
                
                const orb = this.getOrbAt({c: c, r: r});

                if (orb && !orb.displacedByWrappedImplosion) {

                    const deltaX = BoardContainer.ORBS_AFFECTED_BY_WRAPPED_EXPLOSION[i].delta.x * this.wrappedImplosionDisplacementFactor;
                    const deltaY = BoardContainer.ORBS_AFFECTED_BY_WRAPPED_EXPLOSION[i].delta.y * this.wrappedImplosionDisplacementFactor;

                    orb.onWrappedOrbImploded({x: deltaX, y: deltaY});
                }
            }
        }

        this.wrappedImplosionDisplacementFactor *= .35;
    }

    public getOrbAt(p: {c: number, r: number}): Orb {

        let orb: Orb = null;

        for (let i = 0; i < this.orbs.length; i++) {
            if (this.orbs[i].p.c === p.c && this.orbs[i].p.r === p.r) {
                orb = this.orbs[i];
                break;
            }
        }

        return orb;
    }

    public onLevelUp(firstInstantiation?: boolean, newCol?: boolean): void {

        if (!firstInstantiation) {
            this.boardBackground.onLevelUp(newCol);
        }
        
        const hudHeight = 185;
        
        const width = GameVars.cols * Orb.SIZE;
        const height = GameVars.rows * Orb.SIZE; 

        const availableWidth = GameConstants.GAME_WIDTH * .98;
        const availableHeight = (GameConstants.GAME_HEIGHT / GameVars.scaleY - hudHeight) ;

        let scaleX = availableWidth / width;
        let scaleY = availableHeight / height;

        if (height * scaleX < availableHeight) {
            scaleY = scaleX * GameVars.scaleY;
        } else {
            scaleX = scaleY;
            scaleY = scaleY * GameVars.scaleY;
        }

        if (GameConstants.DEBUG_MODE) {
            scaleX *= .75;
            scaleY *= .75;
        }
        
        const x = GameConstants.GAME_WIDTH / 2 - (GameVars.cols - 1) * Orb.SIZE / 2 * scaleX;
        const y = GameConstants.GAME_HEIGHT / 2 - (GameVars.rows - 1) * Orb.SIZE / 2 * scaleY + hudHeight * .5 * GameVars.scaleY; 

        if (firstInstantiation) {

            this.scaleX = scaleX;
            this.scaleY = scaleY;

            this.x = x;
            this.y = y;

        } else {

            this.scene.tweens.add({
                        targets: this,
                        x: x,
                        y: y,
                        scaleX: scaleX,
                        scaleY: scaleY,
                        ease: Phaser.Math.Easing.Cubic.Out,
                        duration: 500
                    });
        }
    }

    public reshuffle (prevBoard: Candy.OrbData[][]): void {

        // ES POSIBLE QUE ESTO SEA INNECESARIO
        // PERO NOS CUBRIMOS LAS ESPALDAS
        const cols = prevBoard.length;
        const rows = prevBoard[0].length;

        for (let c = 0; c < cols; c ++) {
            for (let r = 0; r < rows; r ++) {
                const orb = this.getOrbAt({c: c, r: r});
                orb.orbData = prevBoard[c][r];
            }
        }

        this.orbsMovingCounter = cols * rows;

        for (let i = 0; i < this.orbs.length; i ++) {

            const id = this.orbs[i].orbData.id;

            const orbData = Candy.Utils.getOrbDataById(GameVars.candyEngine.board, id);

            // console.log(this.orbs.length, id, orbData);

            this.orbs[i].orbData = orbData;

            this.orbs[i].reshuffle();
        }
    }

    public onReshuffledOrbReachedItsFinalPosition(): void {

        this.orbsMovingCounter --;

        if (this.orbsMovingCounter === 0) {
            BoardManager.currentInstance.onBoardReshuffled();
        }
    }

    private createAnimations(): void {

        this.scene.anims.create({ key: "popped", frames: this.scene.anims.generateFrameNames( "texture_atlas_7", {prefix: "popped_fx_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 24, hideOnComplete: true});
        this.scene.anims.create({ key: "ray", frames: this.scene.anims.generateFrameNames( "texture_atlas_6", {prefix: "fx_electricity_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: 20, hideOnComplete: true});
        this.scene.anims.create({ key: "simple_wrapped", frames: this.scene.anims.generateFrameNames( "texture_atlas_7", {prefix: "wrapped_explosion_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: 20, hideOnComplete: true});
        this.scene.anims.create({ key: "bomb_transformation", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", {prefix: "color_bomb_transform_", start: 1, end: 13, zeroPad: 2, suffix: ""}), frameRate: 20, hideOnComplete: true});
        this.scene.anims.create({ key: "wrapped_transformation", frames: this.scene.anims.generateFrameNames( "texture_atlas_7", {prefix: "wrapped_transform_", start: 1, end: 17, zeroPad: 2, suffix: ""}), frameRate: 24, hideOnComplete: true});
        this.scene.anims.create({ key: "shine", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", {prefix: "shine_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 24, hideOnComplete: true});
        this.scene.anims.create({ key: "glare", frames: this.scene.anims.generateFrameNames( "texture_atlas_6", {prefix: "glare_tile_", start: 1, end: 13, zeroPad: 2, suffix: ""}), frameRate: 26, hideOnComplete: true});
        this.scene.anims.create({ key: "bomb_implosion", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", {prefix: "color_bomb_implosion_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 30, hideOnComplete: true});
    }
}
